import * as React from 'react';
import { useNavigate } from "react-router-dom";

// Material UI Components
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { green, grey } from '@mui/material/colors';
import GrantAccessButton from '../GrantAccessButton';
import AddTenantButton from '../AddTenantButton';

export default function CompanyNag(props) {
  const { company, openCompanyNag, setOpenCompanyNag } = props;
  const navigate = useNavigate();

  const handleValidate = () => {
    navigate(`/mytenant-validate`);
  };

  if (!company) {
    return null;
  }

  const setupSteps = [
    {
      key: "signup",
      label: "Sign up completed - account created",
      completed: company.setupStageComplete >= 1,
      action:
        company.setupStageComplete === 0 ? (
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/mycompanysetupcreate`)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Complete Initial Setup
          </Button>
        ) : null,
    },
    {
      key: "tenant",
      label: "Configure your M365 Tenant settings for this App",
      completed: company.setupStageComplete >= 2,
      action:
        company.setupStageComplete === 1 ? (
          <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <AddTenantButton />
          </Box>
        ) : null,
    },
    {
      key: "consent",
      label: "Grant this App access to your M365 Tenant (login as your M365 Global Administrator to consent)",
      completed: company.setupStageComplete >= 3,
      action:
        company.setupStageComplete === 2 ? (
          <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <GrantAccessButton company={company} />
          </Box>
        ) : null,
    },
    {
      key: "validate",
      label: "Validate and run first Audit Synch (login as M365 User that was designated as App Administrator)",
      completed: company.setupStageComplete >= 5,
      action:
        company.setupStageComplete === 3 || company.setupStageComplete === 4 ? (
          <Button
            variant="contained"
            size="small"
            onClick={handleValidate}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Validate
          </Button>
        ) : null,
    },
  ];

  return (
    <Stack sx={{ width: '100%', pb: 2 }} spacing={2}>
      <Collapse in={openCompanyNag}>
        {company.setupStageComplete <= 4 ? (
          <Alert severity="warning">
            <AlertTitle>Audit Vault for M365 - Complete Your Setup to Continue</AlertTitle>

            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {setupSteps.map((step) => (
                  <React.Fragment key={step.key}>
                    <Grid item xs={12} sm={1}>
                      {step.completed ? (
                        <CheckCircleOutlineIcon sx={{ color: green[500] }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ color: grey[500] }} />
                      )}
                    </Grid>

                    <Grid item xs={12} sm={7}>
                      <Typography>{step.label}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      {step.action ? (
                        step.action
                      ) : ""}
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          </Alert>
        ) : (
          company.showSetUpNag && (
            <Alert
              severity="success"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpenCompanyNag(false);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ mb: 2 }}
            >
              <AlertTitle>Setup Complete.</AlertTitle>

              <Typography sx={{ pb: 2 }}>
                Congratulations! Audit Vault for M365 is now securely preserving your audit logs from Microsoft 365!
              </Typography>

              <Box>
                <Typography sx={{ pb: 1 }}>
                  Additional Setup Items We Recommend:
                </Typography>
                <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
                  <li>
                    Deploy our "Audit History for SharePoint" App to your SharePoint Online Site Collection(s) from Microsoft AppSource.{" "}
                    <Link onClick={() => navigate(`/Tenant/MyTenant?tab=2`)} component="button">
                      Learn more
                    </Link>
                  </li>
                  <li>
                    Import your Tenant's Historical Microsoft 365 Audit Log Records.{" "}
                    <Link onClick={() => navigate(`/Tenant/MyTenant?tab=5`)} component="button">
                      Learn more
                    </Link>
                  </li>
                </Box>
              </Box>
            </Alert>
          )
        )}
      </Collapse>
    </Stack>
  );
}
